/**
 * Shared useFeatureAccess hook
 * Platform-agnostic tier-based feature gating.
 * Web and mobile provide their own thin wrappers to extract the tier.
 */

import { useMemo } from 'react';
import { canUseFactorById } from '../factorRegistry';
import type { AccessPattern, GatedFeature } from '../featureAccess';
import {
  getAccessPattern,
  getRequiredTier,
  getTrackingLimit,
  hasAccess,
  isFeatureHidden,
  isFeatureLocked,
  isThemeFree,
} from '../featureAccess';
import type { SubscriptionTier } from '../subscriptionTier';
import { factorLimitFor } from '../tierInputPolicy';

export interface UseFeatureAccessOptions {
  tier: SubscriptionTier;
}

export interface UseFeatureAccessResult {
  /** Current subscription tier */
  tier: SubscriptionTier;
  /** Check if a feature is accessible */
  hasAccess: (feature: GatedFeature) => boolean;
  /** Get the UI access pattern for rendering decisions */
  getAccessPattern: (feature: GatedFeature) => AccessPattern;
  /** Check if feature is locked (visible but disabled, click shows upgrade) */
  isLocked: (feature: GatedFeature) => boolean;
  /** Check if feature is hidden (not rendered at all) */
  isHidden: (feature: GatedFeature) => boolean;
  /** Get the minimum tier required for a feature */
  getRequiredTier: (feature: GatedFeature) => 'pro' | 'premium' | 'elite';
  /** Check if a theme is free */
  isThemeFree: (themeId: string) => boolean;
  /** Check if a theme is accessible for this tier */
  canUseTheme: (themeId: string) => boolean;
  /** Daily tracking limit for this tier */
  trackingLimit: number;
  /** Max factor toggles for this tier, as the server enforces it */
  factorLimit: number;
  /** Whether this tier may use a factor toggle, by its registry row (false for an unknown id) */
  canUseFactor: (factorId: string) => boolean;
  /** Whether user is on free tier */
  isFree: boolean;
  /** Whether user is pro or higher */
  isPro: boolean;
  /** Whether user is premium or higher */
  isPremium: boolean;
  /** Whether user is elite or higher */
  isElite: boolean;
  /** Whether user is admin */
  isAdmin: boolean;
}

export function useFeatureAccess({ tier }: UseFeatureAccessOptions): UseFeatureAccessResult {
  return useMemo(
    () => ({
      tier,
      hasAccess: (feature: GatedFeature) => hasAccess(tier, feature),
      getAccessPattern: (feature: GatedFeature) => getAccessPattern(tier, feature),
      isLocked: (feature: GatedFeature) => isFeatureLocked(tier, feature),
      isHidden: (feature: GatedFeature) => isFeatureHidden(tier, feature),
      getRequiredTier,
      isThemeFree,
      canUseTheme: (themeId: string) => isThemeFree(themeId) || hasAccess(tier, 'theme_premium'),
      trackingLimit: getTrackingLimit(tier),
      factorLimit: factorLimitFor(tier),
      canUseFactor: (factorId: string) => canUseFactorById(tier, factorId),
      isFree: tier === 'free',
      isPro: tier === 'pro' || tier === 'premium' || tier === 'elite' || tier === 'admin',
      isPremium: tier === 'premium' || tier === 'elite' || tier === 'admin',
      isElite: tier === 'elite' || tier === 'admin',
      isAdmin: tier === 'admin',
    }),
    [tier],
  );
}
